import { useQuery } from '@apollo/client';
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import {
  BankIcon,
  CaretDownIcon,
  CheckIcon,
  Icon,
  UserIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react';
import { gql } from '__generated__/gql';
import {
  DashboardFundsEnumType,
  DashboardSyndicatesEnumType,
  InvestmentEntityStatusEnumType,
  SyndicateTypeEnumType,
  UserStatusEnumType,
} from '__generated__/graphql';
import classNames from 'classnames';
import { Fragment, useEffect, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';

const PROFILE_SWITCH_BAR_QUERY = gql(/* GraphQL */ `
  query ProfileSwitchBarQuery(
    $filters: DashboardSyndicatesFilterType
    $fundFilters: DashboardFundsFilterType
    $cursor: ID
    $limit: Int
  ) {
    dashboard {
      id
      user {
        id
        name
        status
        image {
          url
        }
        investmentEntities {
          id
          status
          createdAt
        }
      }
      syndicates(filters: $filters, cursor: $cursor, limit: $limit) {
        nodes {
          id
          name
          image
          status
          type
        }
        pageInfo {
          hasNextPage
          cursor
        }
      }
      funds(filters: $fundFilters) {
        nodes {
          id
          name
          image
          status
        }
      }
    }
  }
`);

type ProfileType = 'Syndicate' | 'Fund' | 'Investor' | 'Founder';

const profileTypeIcons: Record<ProfileType, Icon> = {
  Investor: UserIcon,
  Syndicate: UsersThreeIcon,
  Founder: UsersThreeIcon,
  Fund: BankIcon,
};

function ProfileAvatar({
  image,
  name,
  type,
  size = 'md',
  isActive = false,
  ringOffsetClass = 'ring-offset-white',
}: {
  image?: string | null;
  name: string;
  type: ProfileType;
  size?: 'sm' | 'md';
  isActive?: boolean;
  ringOffsetClass?: string;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const FallbackIcon = profileTypeIcons[type];

  useEffect(() => {
    setImageFailed(false);
  }, [image]);

  const containerClass = classNames(
    'shrink-0 overflow-hidden rounded-full bg-gray-100',
    size === 'sm' ? 'h-8 w-8' : 'h-9 w-9',
    isActive && 'ring-2 ring-indigo-500 ring-offset-2',
    isActive && ringOffsetClass
  );

  if (image && !imageFailed) {
    return (
      <div className={containerClass}>
        <img
          src={image}
          alt={name}
          className="h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      </div>
    );
  }

  return (
    <div className={classNames(containerClass, 'flex items-center justify-center')}>
      <FallbackIcon
        size={size === 'sm' ? 16 : 18}
        weight="regular"
        color="currentColor"
        className="text-gray-500"
        aria-hidden
      />
    </div>
  );
}

function getProfileRoute({
  type,
  id,
  userStatus,
  draftEntityId,
}: {
  type: ProfileType;
  id?: string;
  userStatus?: string;
  draftEntityId?: string | null;
}) {
  if (type === 'Syndicate') return `/syndicate-dashboard/${id}`;
  if (type === 'Founder') return `/syndicate-dashboard/${id}/deals`;
  if (type === 'Fund') return `/fund-dashboard/${id}`;
  if (userStatus === 'RUV_ACTIVE' || userStatus === 'RUV_REVIEW') {
    return `/onboarding/complete-profile/investment-entity/${draftEntityId}/investor-application`;
  }
  return '/invest';
}

function ProfileOption({
  id,
  name,
  type,
  image,
  isActive,
  userStatus,
  draftEntityId,
}: {
  id?: string;
  name: string;
  type: ProfileType;
  image?: string | null;
  isActive: boolean;
  userStatus?: string;
  draftEntityId?: string | null;
}) {
  const showCompleteProfile =
    userStatus === UserStatusEnumType.RuvReview || userStatus === UserStatusEnumType.RuvActive;

  return (
    <PopoverButton
      as={NavLink}
      to={getProfileRoute({ type, id, userStatus, draftEntityId })}
      className={classNames(
        'flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors focus:outline-none',
        isActive ? 'bg-gray-100' : 'hover:bg-gray-50'
      )}
    >
      <ProfileAvatar image={image} name={name} type={type} isActive={isActive} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-900">{name}</p>
        <p className="text-xs text-gray-500">{type}</p>
        {showCompleteProfile ? (
          <p className="text-xs font-medium text-indigo-600">Complete profile</p>
        ) : null}
      </div>
      {isActive ? (
        <CheckIcon
          size={16}
          weight="bold"
          color="currentColor"
          className="shrink-0 text-indigo-600"
          aria-hidden
        />
      ) : (
        <span className="h-4 w-4 shrink-0" aria-hidden />
      )}
    </PopoverButton>
  );
}

export default function ProfileSwitchBar() {
  const { syndicateId, fundId } = useParams();

  const [currentProfile, setCurrentProfile] = useState<{
    id: string;
    name: string;
    type: ProfileType;
    image?: string | null;
    userStatus?: string;
  }>();

  const { data, error, loading } = useQuery(PROFILE_SWITCH_BAR_QUERY, {
    variables: {
      filters: {
        type: DashboardSyndicatesEnumType.Lead,
      },
      fundFilters: {
        type: DashboardFundsEnumType.Lead,
      },
    },
  });

  useEffect(() => {
    if (!data) return;

    const syndicates = data.dashboard.syndicates.nodes;
    const funds = data.dashboard.funds.nodes;

    if (syndicateId) {
      const activeSyndicate = syndicates.find(syndicate => syndicate.id === syndicateId);
      if (!activeSyndicate) {
        return;
      }
      const isRuvSyndicate = activeSyndicate.type === SyndicateTypeEnumType.Ruv;
      setCurrentProfile({
        id: activeSyndicate.id,
        name: activeSyndicate.name,
        type: isRuvSyndicate ? 'Founder' : 'Syndicate',
        image: activeSyndicate.image,
      });
    }

    if (fundId) {
      const activeFund = funds.find(fund => fund.id === fundId);
      if (!activeFund) {
        return;
      }
      setCurrentProfile({
        id: activeFund.id,
        name: activeFund.name,
        type: 'Fund',
        image: activeFund.image,
      });
    }

    if (!syndicateId && !fundId) {
      setCurrentProfile({
        id: data.dashboard.user.id,
        name: data.dashboard.user.name,
        type: 'Investor',
        userStatus: data.dashboard.user.status,
        image: data.dashboard.user.image?.url,
      });
    }
  }, [data, fundId, syndicateId]);

  if (loading || error || !data || !currentProfile) return null;

  const syndicates = data.dashboard.syndicates.nodes;
  const funds = data.dashboard.funds.nodes;
  const draftEntity = data.dashboard.user.investmentEntities.find(
    entity => entity.status === InvestmentEntityStatusEnumType.Draft
  );
  const hasOrganizations = syndicates.length > 0 || funds.length > 0;

  return (
    <Popover className="relative pb-3">
      <PopoverButton className="group flex w-full items-center gap-3 rounded-lg bg-gray-200/80 px-2.5 py-2 text-left transition-colors hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 data-[open]:bg-gray-200">
        <ProfileAvatar
          image={currentProfile.image}
          name={currentProfile.name}
          type={currentProfile.type}
          size="sm"
          isActive
          ringOffsetClass="ring-offset-gray-200"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-gray-900">{currentProfile.name}</p>
          <p className="truncate text-xs text-gray-500">{currentProfile.type}</p>
        </div>
        <CaretDownIcon
          size={14}
          weight="bold"
          color="currentColor"
          className="shrink-0 text-gray-500 transition-transform duration-200 group-hover:text-gray-700 group-data-[open]:rotate-180 group-data-[open]:text-indigo-600"
          aria-hidden
        />
      </PopoverButton>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-150"
        enterFrom="opacity-0 translate-y-1 scale-[0.98]"
        enterTo="opacity-100 translate-y-0 scale-100"
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100 translate-y-0 scale-100"
        leaveTo="opacity-0 translate-y-1 scale-[0.98]"
      >
        <PopoverPanel
          anchor="bottom start"
          className="z-50 w-[var(--button-width)] rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl [--anchor-gap:0.375rem]"
        >
          <p className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            Switch profile
          </p>
          <div className="flex max-h-80 flex-col gap-0.5 overflow-y-auto">
            <ProfileOption
              name={data.dashboard.user.name}
              type="Investor"
              isActive={currentProfile.type === 'Investor'}
              userStatus={data.dashboard.user.status}
              draftEntityId={draftEntity?.id}
              image={data.dashboard.user.image?.url}
            />
            {hasOrganizations ? <div className="my-1 border-t border-gray-100" /> : null}
            {syndicates.map(syndicate => (
              <ProfileOption
                key={syndicate.id}
                id={syndicate.id}
                name={syndicate.name}
                type={syndicate.type === SyndicateTypeEnumType.Ruv ? 'Founder' : 'Syndicate'}
                image={syndicate.image}
                isActive={syndicate.id === currentProfile.id}
              />
            ))}
            {funds.map(fund => (
              <ProfileOption
                key={fund.id}
                id={fund.id}
                name={fund.name}
                type="Fund"
                image={fund.image}
                isActive={fund.id === currentProfile.id}
              />
            ))}
          </div>
        </PopoverPanel>
      </Transition>
    </Popover>
  );
}
